import * as React from 'react'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import {
  abortIngestUpload,
  getOntologyBranchHeaderValue,
  isIngestJobFinished,
  useDeleteIngestJobRecordMutation,
  useSwitchBranch
} from '@axilon-apps/domains-ontology-ui-state'
import { Button } from '../ui/button'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '../ui/alert-dialog'

interface IngestJobDeleteDialogProps {
  jobId: string
  /** Names the job in the description. */
  fileName?: string
  /** When this is the active branch, the tab switches to main after the delete. */
  branchId?: string
  jobState: string
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Called after a successful delete, e.g. to leave the deleted job's page. */
  onDeleted?: () => void
}

const STILL_STOPPING_MESSAGE = 'The job is still stopping. Try again in a few seconds.'

/**
 * Confirm dialog for deleting an ingest job. DELETE /ingest/jobs/:id stops the
 * job if it hasn't finished, then removes the job, its history, and the branch
 * it created. A job whose documents are in another branch keeps its record,
 * and the dialog shows the server's reason.
 */
export function IngestJobDeleteDialog({
  jobId,
  fileName,
  branchId,
  jobState,
  open,
  onOpenChange,
  onDeleted
}: IngestJobDeleteDialogProps) {
  const [deleteJob, deleteState] = useDeleteIngestJobRecordMutation()
  const { switchBranch } = useSwitchBranch()
  const [submitError, setSubmitError] = React.useState('')

  const finished = isIngestJobFinished(jobState)
  const submitting = deleteState.isLoading

  React.useEffect(() => {
    if (!open) {
      setSubmitError('')
      deleteState.reset()
    }
  }, [open, deleteState])

  const handleDelete = async () => {
    if (submitting) return
    setSubmitError('')
    abortIngestUpload(jobId)
    try {
      await deleteJob(jobId).unwrap()
      toast.success('Ingest job deleted')
      if (branchId && getOntologyBranchHeaderValue() === branchId) {
        // Switching to main never opens anything, so this cannot fail —
        // don't leave the header pointing at a dropped database.
        void switchBranch(undefined)
      }
      onOpenChange(false)
      onDeleted?.()
    } catch (err) {
      setSubmitError(deleteErrorMessage(err))
    }
  }

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (submitting) return
        onOpenChange(next)
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {finished ? 'Delete ingest job?' : 'Stop and delete ingest job?'}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {fileName ? `${fileName} — ` : ''}
            {finished
              ? 'This permanently deletes the job and its history.'
              : 'This stops the ingest, then permanently deletes the job and its history.'}{' '}
            If the job created its own branch, that branch and its unmerged graph changes are
            deleted too. This cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {submitError ? (
          <p role="alert" className="text-sm text-st-down">
            {submitError}
          </p>
        ) : null}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={submitting}>Cancel</AlertDialogCancel>
          <Button variant="destructive" onClick={handleDelete} disabled={submitting}>
            {submitting ? <Loader2 className="size-4 animate-spin" /> : null}
            {finished ? 'Delete job' : 'Stop and delete'}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

function deleteErrorMessage(err: unknown): string {
  if (!err || typeof err !== 'object') return 'Could not delete the ingest job.'
  const { status, data } = err as { status?: unknown; data?: unknown }
  const body = (data && typeof data === 'object' ? data : {}) as {
    reason?: unknown
    message?: unknown
    error?: unknown
  }
  if (status === 409) {
    if (body.reason === 'JOB_RECORD_PRESERVED' && typeof body.message === 'string') {
      return body.message
    }
    return STILL_STOPPING_MESSAGE
  }
  if (typeof body.message === 'string' && body.message.length > 0) return body.message
  if (typeof body.error === 'string' && body.error.length > 0) return body.error
  return 'Could not delete the ingest job.'
}
